/**
 * Clubs API
 * 
 * Handles club management, membership, and settings.
 */

import { apiGet, apiPost, apiPut, apiDelete } from '../api';

// Import API_BASE_URL for upload endpoints that use FormData
const API_BASE_URL = import.meta.env.VITE_API_URL || import.meta.env.VITE_API_BASE_URL || '/api';

export interface Club {
  id: string;
  name: string;
  description: string | null;
  cover_image_url: string | null;
  hero_image_url: string | null;
  avatar_image_url: string | null; // Issue #562 - Dedicated square avatar
  city: string;
  state: string;
  location?: string; // Combined city/state or full location string
  primary_sport?: string;
  secondary_sports?: string[];
  subscription_level: string | null;
  slug: string;
  owner_id: string;
  visibility: 'public' | 'private' | 'invite_only';
  created_at: string;
}

export interface ClubWithRole extends Club {
  role: 'owner' | 'leader' | 'moderator' | 'member';
  member_count: number;
  upcoming_event_count: number;
}

export interface ClubDetailResponse {
  club: {
    id: string;
    name: string;
    slug: string;
    description: string | null;
    hero_image_url: string | null;
    avatar_image_url: string | null; // Issue #562
    city?: string;
    state?: string;
    location?: string; // Combined city/state or full location
    primary_sport?: string;
    secondary_sports?: string[];
    visibility?: 'public' | 'private' | 'invite_only';
    subscription_level: string | null;
    owner_id: string;
    created_at: number;
    updated_at: number;
  };
  stats: {
    member_count: number;
    upcoming_event_count: number;
  };
  membership: {
    role: 'leader' | 'moderator' | 'member' | 'none';
    is_owner: boolean;
  };
}

export interface CreateClubPayload {
  name: string;
  description?: string;
  city: string;
  state: string;
  cover_image_url?: string;
}

export interface UpdateClubPayload {
  name?: string;
  description?: string;
  city?: string;
  state?: string;
  cover_image_url?: string;
  primary_sport?: string;
  secondary_sports?: string[];
  subscription_level?: string;
  visibility?: 'public' | 'private' | 'invite_only';
}

export interface TransferOwnershipPayload {
  new_owner_id: string;
}

export interface CreateClubResponse {
  club: Club;
}

export interface AdminClubDetailResponse {
  club: {
    id: string;
    name: string;
    slug: string;
    description: string | null;
    city: string;
    state: string;
    visibility: 'public' | 'invite_only' | 'private';
    subscription_level: string;
    hero_image_url: string | null;
    created_at: string;
    member_count: number;
  };
  owner: {
    id: string;
    email: string;
    public_name: string;
    location: string | null;
    created_at: string;
  } | null;
}

export const clubsApi = {
  /**
   * List all clubs for current user
   * GET /clubs
   */
  listMyClubs: () => apiGet<ClubWithRole[]>('/clubs'),

  /**
   * List all clubs on platform (Platform Admin only)
   * GET /clubs/admin/list
   */
  listAllClubs: () => apiGet<ClubWithRole[]>('/clubs/admin/list'),

  /**
   * Get club details for platform admin (metadata + owner info only)
   * GET /clubs/:slug/admin
   */
  getClubForAdmin: (slug: string) => apiGet<AdminClubDetailResponse>(`/clubs/${slug}/admin`),

  /**
   * Get single club by slug
   * GET /clubs/:slug
   */
  getClub: (slug: string) => apiGet<ClubDetailResponse>(`/clubs/${slug}`),

  /**
   * Get single club by ID
   * GET /clubs/id/:id
   */
  getClubById: (id: string) => apiGet<ClubDetailResponse>(`/clubs/id/${id}`),

  /**
   * Create new club
   * POST /clubs
   */
  createClub: (payload: CreateClubPayload) => apiPost<CreateClubResponse>('/clubs', payload),

  /**
   * Update club details
   * PUT /clubs/:slug
   */
  updateClub: (slug: string, payload: UpdateClubPayload) => 
    apiPut<Club>(`/clubs/${slug}`, payload),

  /**
   * Delete club
   * DELETE /clubs/:slug
   */
  deleteClub: (slug: string) => apiDelete<void>(`/clubs/${slug}`),

  /**
   * Join a club
   * POST /clubs/:slug/join
   */
  joinClub: (slug: string) => apiPost<void>(`/clubs/${slug}/join`),

  /**
   * Leave a club
   * POST /clubs/:slug/leave
   */
  leaveClub: (slug: string) => apiPost<void>(`/clubs/${slug}/leave`),

  /**
   * Transfer club ownership
   * POST /clubs/:id/transfer
   */
  transferOwnership: (clubId: string, payload: TransferOwnershipPayload) =>
    apiPost<void>(`/clubs/${clubId}/transfer`, payload),

  /**
   * Upload club hero image
   * POST /upload/club-hero-image/:clubId
   */
  uploadClubHeroImage: async (clubId: string, file: File) => {
    const formData = new FormData();
    formData.append('image', file);
    
    const response = await fetch(`${API_BASE_URL}/upload/club-hero-image/${clubId}`, {
      method: 'POST',
      body: formData,
      credentials: 'include',
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Upload failed' }));
      throw new Error(error.message || 'Failed to upload hero image');
    }
    
    return response.json();
  },

  /**
   * Remove club hero image
   * DELETE /upload/club-hero-image/:clubId
   */
  removeClubHeroImage: async (clubId: string) => {
    const response = await fetch(`${API_BASE_URL}/upload/club-hero-image/${clubId}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Removal failed' }));
      throw new Error(error.message || 'Failed to remove hero image');
    }
    
    return response.json();
  },
};
